import { components } from '@feedfm/clips-services-api';
import { useCallback, useEffect, useState } from 'react';
import { useStudioAuthenticatedContext } from '../../context/StudioContext';
import { useToast } from '../../context/ToastContext';
import { useDebounce } from '../../hooks/useDebounce';
import { useUrlState } from '../../hooks/useUrlState';
import { CollectionSortBy } from '../../util/collectionSort';
import type { CatalogSortColumn } from './CatalogTable.types';
import { VALID_SORT_COLUMNS } from './CatalogTable.types';
import { hasActiveFilters } from './CatalogTable.utils';

type TrackObject = components['schemas']['TrackObject'];
type CatalogFiltersType =
  components['schemas']['CurationCatalogRequest']['filters'];

export interface UseCatalogTableReturn {
  tracks: TrackObject[];
  loading: boolean;
  error: string | null;
  page: number;
  pageSize: number;
  total: number;
  searchQuery: string;
  filters: CatalogFiltersType | undefined;
  sortBy: CatalogSortColumn;
  sortOrder: 'asc' | 'desc';
  setPage: (page: number) => void;
  setPageSize: (size: number) => void;
  setSearchQuery: (query: string) => void;
  setFilters: (
    filters: CatalogFiltersType | undefined,
    resetPage?: boolean,
  ) => void;
  setSortBy: (column: CatalogSortColumn) => void;
  setSortOrder: (order: 'asc' | 'desc') => void;
  handleSort: (column: CatalogSortColumn) => void;
  handlePlayTrack: (track: TrackObject) => void;
  isTrackPlaying: (trackId: number) => boolean;
  applyFilter: (key: string, value: string) => void;
}

export const useCatalogTable = (): UseCatalogTableReturn => {
  const { backendClient, playerState } = useStudioAuthenticatedContext();
  const { showToast } = useToast();
  const { getParam, getNumberParam, setParams } = useUrlState();

  // ─── State ──────────────────────────────────────────────────────────────────

  const [tracks, setTracks] = useState<TrackObject[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(() => (getNumberParam('page', 1) ?? 1) - 1);
  const [pageSize, setPageSize] = useState(
    () => getNumberParam('pageSize', 20) ?? 20,
  );
  const [total, setTotal] = useState(0);
  const [searchQuery, setSearchQuery] = useState(
    () => getParam('search') || '',
  );
  const debouncedSearchQuery = useDebounce(searchQuery, 500);

  const [filters, setFiltersState] = useState<CatalogFiltersType | undefined>(
    () => {
      const urlFilters = getParam('filters');
      if (urlFilters) {
        try {
          return JSON.parse(urlFilters);
        } catch {
          return {};
        }
      }
      return {};
    },
  );

  const [sortBy, setSortBy] = useState<CatalogSortColumn>(() => {
    const urlSortBy = getParam('sortBy') as CatalogSortColumn | null;
    return urlSortBy && VALID_SORT_COLUMNS.includes(urlSortBy)
      ? urlSortBy
      : null;
  });
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>(() => {
    const urlSortOrder = getParam('sortOrder');
    return urlSortOrder === 'desc' ? 'desc' : 'asc';
  });

  // ─── URL Sync ───────────────────────────────────────────────────────────────

  useEffect(() => {
    const hasFiltersActive = hasActiveFilters(filters);
    setParams({
      search: searchQuery || null,
      page: page > 0 ? page + 1 : null,
      pageSize: pageSize !== 20 ? pageSize : null,
      sortBy: sortBy || null,
      sortOrder: sortBy && sortOrder !== 'asc' ? sortOrder : null,
      filters: hasFiltersActive ? JSON.stringify(filters) : null,
    });
  }, [searchQuery, page, pageSize, sortBy, sortOrder, filters, setParams]);

  // ─── Data Fetching ──────────────────────────────────────────────────────────

  const fetchTracks = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const request: components['schemas']['CurationCatalogRequest'] = {
        pagination: {
          limit: pageSize,
          offset: page * pageSize,
        },
        filters: {
          ...filters,
          ...(debouncedSearchQuery ? { textSearch: debouncedSearchQuery } : {}),
        },
        sortBy:
          (sortBy as components['schemas']['CurationCatalogRequest']['sortBy']) ||
          undefined,
        sortOrder,
      };

      const response = await backendClient.getCurationCatalog(request);
      setTracks(response.tracks || []);
      setTotal(response.pagination?.total || 0);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : 'An error occurred while fetching tracks',
      );
      showToast('Failed to fetch tracks', 'error');
    } finally {
      setLoading(false);
    }
  }, [
    backendClient,
    page,
    pageSize,
    debouncedSearchQuery,
    filters,
    sortBy,
    sortOrder,
    showToast,
  ]);

  useEffect(() => {
    fetchTracks();
  }, [fetchTracks]);

  // ─── Playback ───────────────────────────────────────────────────────────────

  const handlePlayTrack = useCallback(
    (track: TrackObject) => {
      if (!track.track_preview?.url) {
        showToast('No preview available for this track', 'warning');
        return;
      }

      const singleTrackCollection: components['schemas']['Collection'] = {
        collectionId: track.id,
        collectionName: track.title,
        description: `Full track: ${track.title}`,
        publishedOn: null,
        rating: 'unknown',
        explicit: false,
        clips: [
          {
            clipId: track.id,
            trackId: track.id,
            title: track.title,
            artist: track.display_artist_name,
            album: track.release_title,
            duration: track.duration_in_seconds,
            preSignedUrl: track.track_preview.url,
            preSignedUrlExpires: '',
            encoding: '',
            bitrate: 0,
            explicitness: track.explicitness
              ? (track.explicitness.toLowerCase() as
                  | 'explicit'
                  | 'clean'
                  | 'censored'
                  | 'unknown')
              : 'unknown',
            artworkPreSignedUrl: track.artwork_url || undefined,
            artworkThumbnailUrl: track.artwork_thumbnail_url ?? null,
            createDate: track.create_date,
          },
        ],
      };

      if (
        playerState.playingItem &&
        'collectionId' in playerState.playingItem &&
        playerState.playingItem.collectionId === track.id &&
        playerState.playIntent
      ) {
        playerState.togglePlayback();
      } else {
        playerState.clickCollection(
          singleTrackCollection,
          CollectionSortBy.CREATE_DATE,
        );
      }
    },
    [playerState, showToast],
  );

  const isTrackPlaying = useCallback(
    (trackId: number): boolean =>
      !!(
        playerState.playingItem &&
        'collectionId' in playerState.playingItem &&
        playerState.playingItem.collectionId === trackId &&
        playerState.playIntent
      ),
    [playerState],
  );

  // ─── Sorting ────────────────────────────────────────────────────────────────

  const handleSort = useCallback(
    (column: CatalogSortColumn) => {
      if (sortBy === column) {
        if (sortOrder === 'asc') {
          setSortOrder('desc');
        } else {
          setSortBy(null);
          setSortOrder('asc');
        }
      } else {
        setSortBy(column);
        setSortOrder('asc');
      }
      setPage(0);
    },
    [sortBy, sortOrder],
  );

  // ─── Click-to-Filter ───────────────────────────────────────────────────────

  const applyFilter = useCallback(
    (key: string, value: string) => {
      setFiltersState((prev) => ({ ...prev, [key]: value }));
      setPage(0);
    },
    [],
  );

  const setFilters = useCallback(
    (newFilters: CatalogFiltersType | undefined, resetPage = true) => {
      setFiltersState(newFilters);
      if (resetPage) {
        setPage(0);
      }
    },
    [],
  );

  return {
    tracks,
    loading,
    error,
    page,
    pageSize,
    total,
    searchQuery,
    filters,
    sortBy,
    sortOrder,
    setPage,
    setPageSize,
    setSearchQuery,
    setFilters,
    setSortBy,
    setSortOrder,
    handleSort,
    handlePlayTrack,
    isTrackPlaying,
    applyFilter,
  };
};
